import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy } from "firebase/firestore";
import React from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import { CORVUS_ORGANIZATION_ID } from "../../api/api.jsx";
import {
    TaskDocument,
    useAppCollection,
    useLocationDocument,
    useOrganizationDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import AppSettingsFormFields, {
    buildAppSettingsSchema,
    getActiveAppSettingsModules,
    getAppSettingsValues,
} from "../../components/AppSettings/AppSettingsFormFields.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";
import { deepMerge, pruneObject } from "../../utils/objectUtils.js";

export default function TaskAppSettingsPage() {
    // load stuff
    const { organizationId, locationId, taskId, appId } = useParams();
    const [organization] = useOrganizationDocument(organizationId, {
        mode: "once",
    });
    const [corvusOrganization] = useOrganizationDocument(
        CORVUS_ORGANIZATION_ID,
        { mode: "once" },
    );
    const [location] = useLocationDocument(organizationId, locationId, {
        mode: "once",
    });
    const [task] = useTaskDocument(organizationId, locationId, taskId, {
        mode: "once",
    });
    const navigate = useNavigate();
    const [apps] = useAppCollection([orderBy("name", "asc")]);
    const app = apps.find((item) => item.id === appId);

    // get the current app from the location and from the task
    const locationApp = location?.apps?.find((a) => a?.id === appId);
    const taskApp = task?.apps?.find((a) => a?.id === appId);

    // get the app setting as they would be if we leave the task empty
    const defaultAppSettings = React.useMemo(
        () => deepMerge(app?.settings || {}, locationApp?.settings || {}),
        [app, locationApp],
    );

    const activeSettingsModules = React.useMemo(
        () => getActiveAppSettingsModules(app, "task", apps),
        [app, apps],
    );

    const settingsSchema = React.useMemo(
        () => buildAppSettingsSchema(activeSettingsModules, z),
        [activeSettingsModules],
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(settingsSchema),
        defaultValues: {
            settings: defaultAppSettings || {},
        },
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const cloudConnections = Object.entries(
        organization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));

    const corvusCloudConnections = Object.entries(
        corvusOrganization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));

    const cloudConnectionIds = new Set(cloudConnections.map((c) => c.id));

    const allCloudConnections = [
        ...cloudConnections,
        ...corvusCloudConnections.filter((c) => !cloudConnectionIds.has(c.id)),
    ];

    const onError = (errors) => {
        console.log("Validation errors:", errors);
    };

    React.useEffect(() => {
        setIsLoading(true);

        reset({
            settings: taskApp
                ? getAppSettingsValues(activeSettingsModules, taskApp)
                : {},
        });
        setIsLoading(false);
    }, [taskApp, reset, activeSettingsModules]);

    const onSubmit = async ({ settings }) => {
        if (!taskApp) {
            throw new Error(`Task app ${appId} not found`);
        }

        // Replace top-level keys
        const updatedSettings = {
            ...(taskApp.settings || {}),
            ...settings,
        };

        taskApp.settings = pruneObject(updatedSettings) || {};

        await TaskDocument(organizationId, locationId, taskId).set(task);
        navigate(urls.task(organizationId, locationId, taskId));
    };

    return (
        <Layout>
            <title>App Settings | MyCorvus</title>
            <Breadcrumbs
                path={breadcrumbPaths.taskAppSettings}
                organization={organization}
                location={location}
                task={task}
                app={app ?? null}
            />
            <Card>
                <Title title={`App Settings`} />
                <Card.Body className="p-4 py-0">
                    {isLoading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit, onError)}>
                            <AppSettingsFormFields
                                activeSettingsModules={activeSettingsModules}
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                defaultAppSettings={defaultAppSettings}
                                extraContext={{
                                    cloudConnections: allCloudConnections,
                                }}
                            />

                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-1"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-1"
                                to={urls.task(
                                    organizationId,
                                    locationId,
                                    taskId,
                                )}
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
